import {
	Box,
	Card,
	CardContent,
	Chip,
	Link,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

import { Loader } from '@/components'
import { useAppContext } from '@/hooks'
import type {
	Permission,
	PermissionEvaluationItemDtoRead,
	PermissionSourceDtoRead,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	useEvaluatePermissionsQuery,
	useGetGroupsQuery,
	useGetUsersQuery,
} from '@/rtkApi/mddApiSliceGeneratedV2'

import {
	ACL_PERMISSIONS,
	type PermissionChoice,
	permissionToLabelKey,
} from './structureAclUtils'

function SourceList({
	items,
	emptyLabel,
	onOpenStructurePermissions,
	noStructureLabel,
}: {
	emptyLabel: string
	items: PermissionSourceDtoRead[]
	noStructureLabel: string,
	onOpenStructurePermissions?: (structureId: number) => void
}) {
	const { t } = useAppContext()
	if (items.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				{emptyLabel}
			</Typography>
		)
	}
	return (
		<Box component="ul" sx={{ m: 0, pl: 2.25, '& li': { mb: 0.5 } }}>
			{items.map((s, i) => {
				const structureLabel =
					s.structure?.name ?? s.structure?.fullPath ?? noStructureLabel
				const suffixParts = [s.groupName].filter(Boolean)
				return (
					<Box key={i} component="li" sx={{ mb: 0.5 }}>
						<Typography variant="body2">
							{s.structure?.id != null && onOpenStructurePermissions ? (
								<Tooltip title={t('ACL_EVALUATE_OPEN_STRUCTURE_PERMISSIONS')}>
									<Link
										component="button"
										variant="body2"
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											onOpenStructurePermissions(s.structure!.id!)
										}}
									>
										{structureLabel}
									</Link>
								</Tooltip>
							) : (
								structureLabel
							)}
							{suffixParts.length > 0 ? ` · ${suffixParts.join(' · ')}` : ''}
						</Typography>
					</Box>
				)
			})}
		</Box>
	)
}

function getPermissionChoice(
	item: PermissionEvaluationItemDtoRead | undefined,
): PermissionChoice {
	const deniedCount = item?.deniedFrom?.length ?? 0
	const allowedCount = item?.allowedFrom?.length ?? 0
	if (deniedCount > 0) return 'deny'
	if (allowedCount > 0) return 'allow'
	return 'inherit'
}

type StructureAclEvaluateTabProps = {
	enabled: boolean
	onOpenStructurePermissions?: (structureId: number) => void
	structureId: number
}

export const StructureAclEvaluateTab = ({
	structureId,
	enabled,
	onOpenStructurePermissions,
}: StructureAclEvaluateTabProps) => {
	const { t } = useAppContext()
	const [forUserId, setForUserId] = useState<number | ''>('')
	const [groupName, setGroupName] = useState('')

	const { data: usersDataPage1, isLoading: usersLoadingPage1 } = useGetUsersQuery(
		{ pageSize: 500, pageNumber: 1 },
		{ skip: !enabled },
	)
	const { data: usersDataPage0, isLoading: usersLoadingPage0 } = useGetUsersQuery(
		{ pageSize: 500, pageNumber: 0 },
		{ skip: !enabled },
	)
	const { data: groupsPage1 = [], isLoading: groupsLoadingPage1 } = useGetGroupsQuery(
		{ pageSize: 1000, pageNumber: 1 },
		{ skip: !enabled },
	)
	const { data: groupsPage0 = [], isLoading: groupsLoadingPage0 } = useGetGroupsQuery(
		{ pageSize: 1000, pageNumber: 0 },
		{ skip: !enabled },
	)

	const users = useMemo(() => {
		const map = new Map<number, NonNullable<typeof usersDataPage1>['content'][number]>()
		for (const u of usersDataPage1?.content ?? []) map.set(u.id, u)
		for (const u of usersDataPage0?.content ?? []) map.set(u.id, u)
		return Array.from(map.values())
	}, [usersDataPage1?.content, usersDataPage0?.content])
	const groups = useMemo(() => {
		const map = new Map<string, (typeof groupsPage1)[number]>()
		for (const g of groupsPage1) if (g.name) map.set(g.name, g)
		for (const g of groupsPage0) if (g.name) map.set(g.name, g)
		return Array.from(map.values())
	}, [groupsPage1, groupsPage0])

	const userDisplayById = useMemo(() => {
		const map = new Map<number, string>()
		for (const u of users) {
			const label = u.compositeName || u.loginName
			if (label) map.set(u.id, label)
		}
		return map
	}, [users])

	const evaluateArg = useMemo(
		() => ({
			structureId,
			forUserId: forUserId === '' ? undefined : forUserId,
			groupName: groupName.trim() || undefined,
		}),
		[structureId, forUserId, groupName],
	)

	const { data, isFetching, isError } = useEvaluatePermissionsQuery(
		evaluateArg,
		{ skip: !enabled || !structureId },
	)

	const rowsByPermission = useMemo(() => {
		const map = new Map<Permission, PermissionEvaluationItemDtoRead>()
		for (const item of data?.permissions ?? []) {
			if (item.permission) map.set(item.permission, item)
		}
		return map
	}, [data?.permissions])

	const listLoading =
		usersLoadingPage1 ||
		usersLoadingPage0 ||
		groupsLoadingPage1 ||
		groupsLoadingPage0

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
			<Typography variant="body2" color="text.secondary">
				{t('ACL_EVALUATE_DESCRIPTION')}
			</Typography>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
				<TextField
					select
					label={t('ACL_EVALUATE_USER')}
					value={forUserId === '' ? '' : String(forUserId)}
					onChange={(e) => {
						const v = e.target.value
						setForUserId(v === '' ? '' : Number(v))
					}}
					size="small"
					sx={{ minWidth: 260 }}
					disabled={listLoading}
					slotProps={{
						select: {
							displayEmpty: true,
							renderValue: (selected) => {
								if (selected === '') return t('ACL_EVALUATE_USER_CURRENT')
								const id = Number(selected)
								const label = userDisplayById.get(id)
								if (label) return label
								const u = users.find((x) => x.id === id)
								return u?.compositeName || u?.loginName || String(selected)
							},
							MenuProps: {
								disableScrollLock: true,
								disableEnforceFocus: true,
								slotProps: {
									paper: {
										sx: (theme) => ({
											zIndex: theme.zIndex.modal + 100,
										}),
									},
								},
							},
						},
					}}
				>
					<MenuItem value="">
						<em>{t('ACL_EVALUATE_USER_CURRENT')}</em>
					</MenuItem>
					{users.map((u) => (
						<MenuItem key={u.id} value={String(u.id)}>
							{u.compositeName || u.loginName}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					label={t('GROUP')}
					value={groupName}
					onChange={(e) => setGroupName(e.target.value)}
					size="small"
					sx={{ minWidth: 220 }}
					disabled={listLoading}
					helperText={t('ACL_EVALUATE_GROUP_HINT')}
					slotProps={{
						select: {
							displayEmpty: true,
							renderValue: (selected) =>
								selected === '' ? t('ACL_EVALUATE_GROUP_ANY') : String(selected),
							MenuProps: {
								disableScrollLock: true,
								disableEnforceFocus: true,
								slotProps: {
									paper: {
										sx: (theme) => ({
											zIndex: theme.zIndex.modal + 100,
										}),
									},
								},
							},
						},
					}}
				>
					<MenuItem value="">
						<em>{t('ACL_EVALUATE_GROUP_ANY')}</em>
					</MenuItem>
					{groups
						.filter((g) => (g.name ?? '').length > 0)
						.map((g) => (
							<MenuItem key={g.id ?? g.name} value={g.name!}>
								{g.name}
							</MenuItem>
						))}
				</TextField>
			</Box>

			{isFetching ? (
				<Loader loaded={false} />
			) : isError ? (
				<Typography color="error">{t('ACL_EVALUATE_ERROR')}</Typography>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1.5,
						minHeight: 0,
						overflow: 'auto',
						maxWidth: '100%',
					}}
				>
					{ACL_PERMISSIONS.map((p) => {
						const row = rowsByPermission.get(p)
						const allowed = row?.allowedFrom ?? []
						const denied = row?.deniedFrom ?? []
						const choice = getPermissionChoice(row)
						const choiceLabel = t(
							choice === 'allow'
								? 'ACL_PERMISSION_ALLOW'
								: choice === 'deny'
									? 'ACL_PERMISSION_DENY'
									: 'ACL_PERMISSION_INHERIT',
						)
						return (
							<Card key={p} variant="outlined" sx={{ overflow: 'visible' }}>
								<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'flex-start',
											gap: 2,
											flexWrap: 'wrap',
										}}
									>
										<Box sx={{ minWidth: 170, flex: '0 1 220px' }}>
											<Typography
												variant="h6"
												sx={{ mb: 0.5, fontSize: '1.05rem', lineHeight: 1.2 }}
											>
												{t(permissionToLabelKey[p])}
											</Typography>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
												<Chip
													size="small"
													label={choiceLabel}
													color={
														choice === 'allow'
															? 'success'
															: choice === 'deny'
																? 'error'
																: 'default'
													}
													variant="outlined"
												/>
											</Box>
										</Box>
										<Box sx={{ minWidth: 260, flex: '1 1 320px' }}>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
											>
												{t('ACL_EVALUATE_ALLOWED_FROM')}
											</Typography>
											<SourceList
												items={allowed}
												emptyLabel={t('ACL_EVALUATE_NO_SOURCES')}
												onOpenStructurePermissions={onOpenStructurePermissions}
												noStructureLabel={t('ACL_EVALUATE_NO_STRUCTURE')}
											/>
										</Box>
										<Box sx={{ minWidth: 260, flex: '1 1 320px' }}>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
											>
												{t('ACL_EVALUATE_DENIED_FROM')}
											</Typography>
											<SourceList
												items={denied}
												emptyLabel={t('ACL_EVALUATE_NO_SOURCES')}
												onOpenStructurePermissions={onOpenStructurePermissions}
												noStructureLabel={t('ACL_EVALUATE_NO_STRUCTURE')}
											/>
										</Box>
									</Box>
								</CardContent>
							</Card>
						)
					})}
				</Box>
			)}
		</Box>
	)
}
